/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Company } from '@centecomm/portal-service';
import { Form, Formik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { TEMP_DIRECT_LINK, DIRECT_LINK } from 'Shared/constants';
import { useHistory } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Yup from 'yup';
import { useLocalStorage } from 'react-use';
import { FlexWrapper } from '../Shared/FlexWrapper';
import { FormButtons, FormInputs } from '../Shared/Forms';
import usePECOAuth0OrgBypass from '../utils/Hooks/usePECOAuth0OrgBypass';
import useSubscription from './Subscription/useSubscription';
import styles from './Login.module.css';

// 1.   IF org in localstorage
//      - set subscription (localstorage and subscription hook)
//      - route to app (pulls from subscription)
// 2.   IF NO org in localstorage
//      - enter email
//      a. IF user has tenant property
//          - set subscription (tenant org id)
//          - route to app (pulls from subscription)
//      b. IF company.length === 1
//          - set subscription (localstorage and subscription hook)
//          - route to app (pulls from subscription)
//      c. IF company.length > 1
//          - select company
//          - set subscription (localstorage and subscription hook)
//          - route to app (pulls from subscription)

function UserSelect(): React.ReactElement {
    const { push } = useHistory();

    const { email: localUserEmail, setEmail, getOrganization, getUserOrganization, setOrganization, setOrganizationLogo } = useSubscription();

    const [organizationIsSet, toggleOrganizationIsSet] = useState<boolean>(false);
    const [userCompanies, setUserCompanies] = useState<Company[]>([]);
    const [userAttempts, setUserAttempts] = useLocalStorage<number>('user-attempts', 0);
    const [showCaptcha, setShowCaptcha] = useState<boolean>(false);
    const [captchaCompleted, setCaptchaCompleted] = useState<boolean>(false);
    const [loginError, setLoginError] = useState<string | null>(null);

    const captchaRef = React.createRef<ReCAPTCHA>();

    // PECO Login bypass logic - This gets executed as a layout effect and takes immediate effect
    // to prevent the user from seeing the login screen if they have the org query param in the URL.
    usePECOAuth0OrgBypass(userCompanies);

    useEffect(() => {
        const directLink = sessionStorage.getItem(TEMP_DIRECT_LINK);
        if (directLink) sessionStorage.setItem(DIRECT_LINK, directLink);
    }, []);

    useEffect(() => {
        if (!organizationIsSet) {
            const localOrgId: string | null = getOrganization();

            if (localOrgId) {
                setOrganization(localOrgId);
                push('/');
            } else toggleOrganizationIsSet(true);
        }
    }, [organizationIsSet]);

    const submitHandler = async ({ email }) => {
        setUserAttempts(userAttempts + 1);

        let userOrganization: string | Company[] | null = null;

        try {
            userOrganization = await getUserOrganization(encodeURIComponent(email));
            setLoginError(null);
        } catch (error) {
            if (error.response && error.response.status === 403) {
                setLoginError('You do not have access to this application.');
            }
            else {
                setLoginError(null);
            }
        }

        if (!userOrganization) {
            return;
        }

        setEmail(email);

        // userType: Tenant
        if (typeof userOrganization === 'string') {
            setOrganization(userOrganization);
            push('/');
        }
        // userType: OwnerOperator
        if (Array.isArray(userOrganization) && userOrganization.length === 1) {
            const { orgId, logoUrl } = userOrganization[0];
            setOrganization(orgId);
            setOrganizationLogo(logoUrl);
            push('/');
        }
        setUserCompanies(userOrganization as Company[]);
    };

    const selectCompany = useCallback((orgId: string, logoUrl: string) => {
        setOrganization(orgId);
        setOrganizationLogo(logoUrl);
        push('/');
    }, []);

    const onChangeCaptcha = (value: string) => {
        if (value) {
            setCaptchaCompleted(true);
        }
    };

    useEffect(() => {
        if (userAttempts === 5) {
            setShowCaptcha(true);
        } else if (userAttempts > 5) {
            setUserAttempts(0);
            setShowCaptcha(false);
            setCaptchaCompleted(false);
            captchaRef.current.reset();
        }
    }, [userAttempts]);

    if (userCompanies.length > 1) {
        return (
            <FlexWrapper column fullWidth align="center" justify="center" className={styles.loginPage}>
                <div className={styles.companyListContainer}>
                    <div className={styles.companyList}>
                        <h1>Select a Company</h1>
                        <p>There are multiple companies associated to your account.</p>
                        <p>Which would you like to login to?</p>
                        <FlexWrapper column justify="center" align="center">
                            {userCompanies.map(({ orgId, name, logoUrl }) => (
                                <div
                                    key={orgId}
                                    style={{
                                        width: '100%',
                                        marginTop: '2rem',
                                        border: '1px solid black',
                                        padding: '2rem',
                                        cursor: 'pointer',
                                    }}
                                    data-qa={`user-company-list-${orgId}`}
                                    onClick={() => selectCompany(orgId, logoUrl)}
                                >
                                    <p style={{ textAlign: 'center' }}>{name}</p>
                                    <p style={{ textAlign: 'center' }}>
                                        <img src={logoUrl} alt={name} style={{ maxWidth: '10rem', marginTop: '.75rem' }} />
                                    </p>
                                </div>
                            ))}
                        </FlexWrapper>
                    </div>
                </div>
            </FlexWrapper>
        );
    }

    return (
        <FlexWrapper column fullWidth align="center" justify="center" className={styles.loginPage}>
            <div className={styles.loginContainer}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '1rem',
                    }}
                >
                    <img
                        src="https://s3.amazonaws.com/tenant-portal.idplans.com/assets/logos/IDTenant-Logo_1000.png"
                        alt="ID plans logo"
                        className={styles.logo}
                    />
                </div>
                <Formik
                    initialValues={{ email: localUserEmail || '' }}
                    onSubmit={submitHandler}
                    validationSchema={Yup.object({
                        email: Yup.string().email().required('Enter a valid email'),
                    })}
                >
                    {({ isSubmitting, isValid }) => (
                        <Form style={{ width: '100%', maxWidth: '30.5rem' }}>
                            <FormInputs.Email
                                id="email"
                                name="email"
                                label="Email"
                                fullWidth
                                required
                                placeholder="Enter your email"
                            />
                            {loginError && (
                                <div className={styles.errorMessage} data-qa="login-error-message">
                                    {loginError}
                                </div>
                            )}
                            {showCaptcha ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        marginTop: '1rem',
                                    }}
                                >
                                    <ReCAPTCHA
                                        type="image"
                                        theme="light"
                                        size="normal"
                                        sitekey={`${CAPTCHA_API_KEY}`}
                                        ref={captchaRef}
                                        onChange={onChangeCaptcha}
                                    />
                                </div>
                            ) : null}
                            <FormButtons.Submit
                                text="Continue"
                                disable={isSubmitting || !isValid || (!captchaCompleted && showCaptcha)}
                                fullWidth
                                withMarginTop
                                className={styles.btnLogin}
                            />
                        </Form>
                    )}
                </Formik>
            </div>
        </FlexWrapper>
    );
}

export default UserSelect;
